import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { Observable, Subscription, catchError, of, switchMap, tap, timer } from 'rxjs';
import { environment } from '../../environments/environment';
import { TaskStatusResponse } from '../shared/interfaces'; // Aggiusta il path se necessario

@Injectable({
  providedIn: 'root',
})
export class TaskService {
  private readonly documentsApiUrl = `${environment.apiUrl}/documents`; // Cambialo se l'endpoint dei task è diverso (es. /tasks/status)
  private readonly platformId = inject(PLATFORM_ID);
  private readonly http = inject(HttpClient);

  private readonly STORAGE_KEY = 'active_scan_task_id';
  private pollingSubscription?: Subscription;

  // Signal Globale per lo stato del task
  public taskState = signal<TaskStatusResponse | null>(null);

  // Computed per sapere se è in caricamento
  public isProcessing = computed(() => {
    const status = this.taskState()?.status;
    return status === 'PENDING' || status === 'IN_PROGRESS';
  });

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      const savedTaskId = localStorage.getItem(this.STORAGE_KEY);

      if (savedTaskId) {
        this.taskState.set({
          id: savedTaskId,
          status: 'IN_PROGRESS', 
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });

        this.startPolling(savedTaskId);
      }
    }
  }

  // Registra e fa partire un nuovo task
  public registerNewTask(taskId: string | number) {
    const safeTaskId = String(taskId);
    localStorage.setItem(this.STORAGE_KEY, safeTaskId);
    
    this.taskState.set({
      id: safeTaskId,
      status: 'IN_PROGRESS',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    
    this.startPolling(safeTaskId);
  }

  // Ignora e chiudi il task corrente
  public dismissTask() {
    this.stopPolling();
    localStorage.removeItem(this.STORAGE_KEY);
    this.taskState.set(null);
  }

  private startPolling(taskId: string) {
    if (this.pollingSubscription && !this.pollingSubscription.closed) return;

    this.pollingSubscription = timer(0, 10000)
      .pipe(
        switchMap(() => this.getTaskStatusApi(taskId).pipe(
          catchError((err) => {
            console.warn('Polling fallito: Task non trovato o errore server.', err);
            if (err.status === 404) {
              const current = this.taskState();
              if (current) this.taskState.set({ ...current, status: 'FAILED' });
              this.stopPolling(); 
            }            
            return of(null); 
          })
        )),
        tap((response: TaskStatusResponse | null) => {
          if (!response) return; 

          this.taskState.set(response);

          if (response.status === 'COMPLETED' || response.status === 'FAILED') {
            this.stopPolling();
          }
        })
      ).subscribe();
  }

  private stopPolling() {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = undefined;
  }

  private getTaskStatusApi(taskId: string): Observable<TaskStatusResponse> {
    return this.http.get<TaskStatusResponse>(
      `${this.documentsApiUrl}/status/${taskId}`
    );
  }
}